import React from 'react';
import {
  Navigate,
  SentryRoutes,
  Route,
  NavigateWildCard,
  AppendNavigate,
  NavigateParsedParams,
} from '@common/components';

import { PrivateRoute } from '@routes/private-route';
import { FourZeroFourPage } from '@identity/pages';
import { OrganizationAdminSettingsRoutes } from './organization-admin-settings-routes';

import { ReportsPage } from 'works/legacy/pages/organization/reports';
import { OrganizationTeamPage } from 'works/legacy/pages/organization/team';
import { StaffingRequestsPage } from 'works/legacy/pages/organization/staffing-requests';
import { OrganizationStaffingRequestPage } from 'works/legacy/pages/organization/staffing-requests/staffing-request';
import {
  ShiftPage,
  WorkerDetailsPage,
  WorkerDetailsRedirectionPage,
} from '@scheduling/pages/organization';
import { ShiftsAddMultipleSteps } from '@scheduling/components';
import { CreateStaffingRequestSelectTemplatePage } from 'works/legacy/pages/organization/staffing-requests/create-staffing-request-select-template';
import { CreateStaffingRequestFormPage } from 'works/legacy/pages/organization/staffing-requests/create-staffing-request-form';
import { ApplicationsPage } from 'works/legacy/pages/organization/applications';
import { OrganizationCandidatePage } from 'works/legacy/pages/organization/applications/candidate';
import { AccountSettingsPage } from '@identity/pages/account-settings-page';
import { TermsAndConditionsPage } from '@identity/pages/terms-and-conditions-page';
import { BillingPage, InvoicePage } from '@pay-bill/pages/organization';
import {
  TimesheetsPage,
  WorkerTimesheetPage,
} from '@timekeeping/pages/organization';
import { OrganizationCompliancePage } from '@onboarding/pages/organization/organization-compliance-page';
import { WorkerUploadSteps } from '@scheduling/components/worker-upload-steps';
import { HomePage } from '@institutions/pages/home-page';
import { useCurrentMembership } from '@identity/hooks';
import {
  MissionControlStaffingPage,
  MissionControlCompliancePage,
  MissionControlBillingPage,
} from '@institutions/pages';
import { CandidateAssignmentPage } from 'works/legacy/components/candidate-assignment';

export function OrganizationRoutes() {
  const currentMembership = useCurrentMembership();

  const longTermEnabled = currentMembership.canShowLongTermAssignments;
  const canShowCompliancePage = currentMembership.canShowSidebarOnboardingItem;
  const canShowShifts = currentMembership.canShowShifts;
  const canShowSidebarTimesheetsItem =
    currentMembership.canShowSidebarTimesheetsItem;
  const canShowMissionControl = currentMembership.canShowMissionControl;

  return (
    <SentryRoutes>
      {/* ADMIN SETTINGS */}

      <Route
        path="/works/admin/*"
        element={<OrganizationAdminSettingsRoutes />}
      />

      {/* COMPLIANCE */}

      <Route
        path="/works/compliance"
        element={<Navigate to="/works/compliance/expired" />}
      />
      <Route
        path="/works/compliance/:tabKey"
        element={
          <PrivateRoute
            menuItem="compliance"
            page={OrganizationCompliancePage}
            permitted={canShowCompliancePage}
          />
        }
      />

      {/* TEAM / ASSIGNMENTS */}

      <Route
        path="/works/team/on_demand/upload"
        element={<PrivateRoute page={WorkerUploadSteps} />}
      />

      <Route
        path="/works/team/on_demand/:id/:tabKey/:workerAssignmentId/:workerAssignmentTabKey" // workerAssignmentTabKey: assignment | compliance | info | profile | notes
        element={
          <PrivateRoute page={CandidateAssignmentPage} menuItem="team" />
        }
      />
      <Route
        path="/works/team/on_demand/:id/:tabKey/:workerAssignmentId"
        element={<AppendNavigate append="assignment" />}
      />

      <Route
        path="/works/team/on_demand/:id"
        element={
          <PrivateRoute page={WorkerDetailsRedirectionPage} menuItem="team" />
        }
      />
      <Route
        path="/works/team/on_demand/:id/:tabKey" // tabKey: units | assignments | on_demand | matches | history
        element={<PrivateRoute page={WorkerDetailsPage} menuItem="team" />}
      />

      <Route
        path="/works/team/long_term/:id"
        element={<AppendNavigate append="assignment" />}
      />
      <Route
        path="/works/team/long_term/:id/:tabKey" // tabKey: assignment | compliance | info | profile | notes
        element={
          <PrivateRoute page={CandidateAssignmentPage} menuItem="team" />
        }
      />

      <Route
        path="/works/team"
        element={(() => {
          const defaultTab = longTermEnabled ? 'long_term' : 'on_demand';
          return <Navigate to={`/works/team/${defaultTab}`} />;
        })()}
      />
      <Route
        path="/works/team/:type" // type: long_term | on_demand
        element={<PrivateRoute page={OrganizationTeamPage} menuItem="team" />}
      />

      {/* SHIFTS */}

      <Route
        path="/works/shifts/feed/add-multiple"
        element={
          <PrivateRoute
            page={ShiftsAddMultipleSteps}
            menuItem="shift"
            permitted={canShowShifts}
          />
        }
      />

      <Route
        path="/works/shifts"
        element={<AppendNavigate append="calendar" />}
      />
      <Route
        path="/works/shifts/:type" // type: calendar | feed | messages
        element={
          <PrivateRoute
            page={ShiftPage}
            menuItem="shift"
            permitted={canShowShifts}
          />
        }
      />

      {/* STAFFING REQUESTS */}

      <Route
        path="/works/staffing-requests/:staffingRequestId/candidates/:id"
        element={<AppendNavigate append="summary" />}
      />
      <Route
        path="/works/staffing-requests/:staffingRequestId/candidates/:id/:tab" // tab: summary | notes
        element={
          <PrivateRoute page={OrganizationCandidatePage} menuItem="staffing" />
        }
      />

      <Route
        path="/works/staffing-requests/templates"
        element={<AppendNavigate append="long_term" />}
      />
      <Route
        path="/works/staffing-requests/templates/:tabKey" // tabKey: long_term | on_demand
        element={
          <PrivateRoute
            page={CreateStaffingRequestSelectTemplatePage}
            menuItem="staffing"
          />
        }
      />

      <Route
        path="/works/staffing-requests/new/:templateId"
        element={
          <PrivateRoute
            page={CreateStaffingRequestFormPage}
            menuItem="staffing"
          />
        }
      />

      <Route
        path="/works/staffing-requests/requests"
        element={<AppendNavigate append="long_term" />}
      />
      <Route
        path="/works/staffing-requests/requests/:tabKey" // tabKey: long_term | on_demand
        element={
          <PrivateRoute page={StaffingRequestsPage} menuItem="staffing" />
        }
      />

      <Route
        path="/works/staffing-requests/:id"
        element={<AppendNavigate append="submitted" />}
      />
      <Route
        path="/works/staffing-requests/:id/:tabKey" // tabKey: submitted | in-review | offer | accepted | withdrawn | rejected
        element={
          <PrivateRoute
            page={OrganizationStaffingRequestPage}
            menuItem="staffing"
          />
        }
      />

      <Route
        path="/works/staffing-requests"
        element={<AppendNavigate append="requests" />}
      />

      {/* APPLICATIONS */}

      <Route
        path="/works/applications/:id"
        element={<AppendNavigate append="summary" />}
      />
      <Route
        path="/works/applications/:id/:tab" // tab: summary | notes
        element={
          <PrivateRoute
            page={OrganizationCandidatePage}
            menuItem="applications"
          />
        }
      />

      <Route
        path="/works/applications"
        element={
          <PrivateRoute page={ApplicationsPage} menuItem="applications" />
        }
      />

      {/* REPORTS */}

      <Route
        path="/works/reports"
        element={<PrivateRoute page={ReportsPage} menuItem="reports" />}
      />

      {/* ACCOUNT SETTINGS */}

      <Route
        path="/works/account-settings"
        element={
          <Navigate to="/works/account-settings/user-settings" replace />
        }
      />

      <Route
        path="/works/account-settings"
        element={<AppendNavigate append="user-settings" />}
      />
      <Route
        path="/works/account-settings/:page" // page: user-settings | change-password | notifications
        element={<PrivateRoute page={AccountSettingsPage} />}
      />

      {/* BILLING */}

      <Route
        path="/works/billing/invoices/:id"
        element={<AppendNavigate append="summary" />}
      />
      <Route
        path="/works/billing/invoices/:id/:tabKey" // tabKey: summary | hours | disputes | history
        element={<PrivateRoute page={InvoicePage} menuItem="billing" />}
      />

      <Route
        path="/works/billing"
        element={<AppendNavigate append="invoices" />}
      />
      <Route
        path="/works/billing/:tab" // tab: invoices | disputes
        element={<PrivateRoute page={BillingPage} menuItem="billing" />}
      />

      {/* TIMECARDS */}

      <Route
        path="/works/timecards"
        element={<AppendNavigate append="submitted" />}
      />
      <Route
        path="/works/timecards/:tab" // tab: not_submitted | submitted | approved | processing | invoiced | expired | rejected | overdue | all
        element={
          <PrivateRoute
            menuItem="timecards"
            page={TimesheetsPage}
            permitted={canShowSidebarTimesheetsItem}
          />
        }
      />

      <Route
        path="/works/timecard/:id"
        element={
          <PrivateRoute
            menuItem="timecards"
            page={WorkerTimesheetPage}
            permitted={canShowSidebarTimesheetsItem}
          />
        }
      />

      {/* HOME / MISSION CONTROL */}

      {canShowMissionControl ? (
        <>
          <Route
            path="/works"
            element={<AppendNavigate append="home/staffing/approvals" />}
          />
          <Route
            path="/works/home"
            element={<AppendNavigate append="staffing/approvals" />}
          />
          <Route
            path="/works/home/staffing"
            element={<AppendNavigate append="approvals" />}
          />
          <Route
            path="/works/home/staffing/:selectedTab" // selectedTab: approvals | requests | candidates | reviews | offers
            element={
              <PrivateRoute page={MissionControlStaffingPage} menuItem="home" />
            }
          />

          <Route
            path="/works/home/compliance"
            element={<AppendNavigate append="missing" />}
          />
          <Route
            path="/works/home/compliance/:selectedTab" // selectedTab: missing | expiring | expired
            element={
              <PrivateRoute
                page={MissionControlCompliancePage}
                menuItem="home"
              />
            }
          />

          <Route
            path="/works/home/billing"
            element={<AppendNavigate append="open" />}
          />
          <Route
            path="/works/home/billing/:selectedTab" // selectedTab: open | approved
            element={
              <PrivateRoute page={MissionControlBillingPage} menuItem="home" />
            }
          />
        </>
      ) : (
        <Route
          path="/works"
          element={<PrivateRoute page={HomePage} menuItem="home" />}
        />
      )}

      <Route
        path="/works/terms-and-conditions"
        element={
          <PrivateRoute page={TermsAndConditionsPage} menuItem="admin" />
        }
      />

      <Route path="*" element={<FourZeroFourPage />} />

      {/**
       * [BEGIN] Legacy URLs redirecting
       * */}

      <Route
        path="/works/staffing_requests/*"
        element={<NavigateWildCard to="/works/staffing-requests/*" />}
      />

      <Route
        path="/works/shifts/calendar/view/:id"
        element={
          <NavigateParsedParams to="/works/shifts/calendar?dialog=shifts-request-details-drawer&shifts-request-details-drawer.shiftId=:id" />
        }
      />
      <Route
        path="/works/candidates/*"
        element={<NavigateWildCard to="/works/applications/*" />}
      />
      <Route
        path="/works/onboarding/*"
        element={<NavigateWildCard to="/works/team/*" />}
      />

      {/**
       * [END] Legacy URLs redirecting
       * */}
    </SentryRoutes>
  );
}
